/* @jsx h */
import { defineComponent, h, computed } from "vue";
import {
  useLocalization,
  LemmonChart,
  type LemmonChartValueFormat,
} from "@lemmonhq/uikit";
import { formatValue, CHART_PALETTE, type CellFormat } from "../format";

type SeriesSpec = { key: string; labelKey: string; format: CellFormat };
type ChartSpec = {
  type: "line" | "bar" | "pie";
  variant?: "default" | "ranking";
  orientation?: "vertical" | "horizontal";
  maxItems?: number;
  showPercentage?: boolean;
  percentageTotalPath?: string;
  dataPath: string;
  x?: string;
  titleKey?: string;
  descriptionKey?: string;
  valueLabels?: boolean;
  compactLabels?: boolean;
  valueAxis?: boolean;
  xLabelKey?: string;
  series: SeriesSpec[];
};

/** `CellFormat` has a few textual variants (date/timestamp/text) a chart axis never plots. */
const toChartFormat = (format: CellFormat): LemmonChartValueFormat =>
  format === "money" || format === "integer" || format === "percentage"
    ? format
    : "decimal";

export const ReportChart = defineComponent({
  name: "DataRoomReportChart",
  props: {
    spec: { type: Object, required: true },
    summary: { type: Object, default: null },
    currency: { type: String, default: "CHF" },
    loading: { type: Boolean, default: false },
  },
  setup(props: any) {
    const { t } = useLocalization();

    const valueAtPath = (source: unknown, path: string): unknown =>
      path.split(".").reduce<unknown>((value, key) => {
        if (value == null || typeof value !== "object") return undefined;
        return (value as Record<string, unknown>)[key];
      }, source);

    const spec = computed<ChartSpec>(() => props.spec);
    const allRows = computed<Array<Record<string, unknown>>>(() => {
      const value = valueAtPath(props.summary, spec.value.dataPath);
      return Array.isArray(value) ? value : [];
    });
    const data = computed<Array<Record<string, unknown>>>(() =>
      spec.value.maxItems
        ? allRows.value.slice(0, spec.value.maxItems)
        : allRows.value,
    );
    /** Truthful disclosure when maxItems hides rows, rather than silent truncation. */
    const hiddenCount = computed(() =>
      Math.max(0, allRows.value.length - data.value.length),
    );
    const xKey = computed(() => spec.value.x ?? "bucket");

    const maxValue = computed(() => {
      let max = 0;
      for (const d of data.value) {
        for (const s of spec.value.series) {
          max = Math.max(max, Math.abs(Number(d[s.key]) || 0));
        }
      }
      return max || 1;
    });

    const percentageTotal = computed(() => {
      if (!spec.value.percentageTotalPath) return 0;
      return Math.abs(
        Number(valueAtPath(props.summary, spec.value.percentageTotalPath)) || 0,
      );
    });

    const percentageFor = (value: unknown) =>
      percentageTotal.value > 0
        ? (Math.abs(Number(value) || 0) / percentageTotal.value) * 100
        : 0;

    /** Leaderboard-style bar length — the top row fills the track (100%), matching the PDF ranking chart. */
    const barWidthFor = (value: unknown) =>
      Math.max(
        0,
        Math.min(100, (Math.abs(Number(value) || 0) / maxValue.value) * 100),
      );

    /** Full-precision tooltip — the compact label on the mark is never the only source. */
    const tooltipFor = (datum: Record<string, unknown>) =>
      [
        String(datum[xKey.value] ?? ""),
        ...spec.value.series.map(
          (s) =>
            `${t(s.labelKey)}: ${formatValue(datum[s.key], s.format, props.currency)}`,
        ),
      ]
        .filter(Boolean)
        .join("\n");

    const renderLegend = () => (
      <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {spec.value.series.map((s, i) => (
          <span
            key={s.key}
            class="flex items-center gap-1.5 text-xs text-base-content/75"
          >
            <span
              class="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }}
            />
            {t(s.labelKey)}
          </span>
        ))}
      </div>
    );

    const renderHorizontalBars = () => (
      <div class="flex flex-col gap-3">
        {data.value.map((datum, index) => (
          <div key={index} class="grid gap-1.5" title={tooltipFor(datum)}>
            <div class="flex items-baseline justify-between gap-3 text-sm">
              <span class="min-w-0 truncate font-medium text-accent">
                {String(datum[xKey.value] ?? "—")}
              </span>
              {spec.value.series.map((series) => (
                <span
                  key={series.key}
                  class="shrink-0 font-semibold tabular-nums text-accent"
                >
                  {formatValue(
                    datum[series.key],
                    series.format,
                    props.currency,
                  )}
                </span>
              ))}
            </div>
            {spec.value.series.map((series, seriesIndex) => (
              <div
                key={series.key}
                class={[
                  "relative overflow-hidden rounded-full bg-base-200",
                  spec.value.showPercentage ? "h-6" : "h-2.5",
                ]}
              >
                <div
                  class="absolute inset-y-0 left-0 rounded-full transition-[width] duration-300"
                  style={{
                    width: `${barWidthFor(datum[series.key])}%`,
                    background:
                      CHART_PALETTE[seriesIndex % CHART_PALETTE.length],
                  }}
                />
                {spec.value.showPercentage && (
                  <span
                    class="absolute top-1/2 -translate-x-full -translate-y-1/2 whitespace-nowrap rounded-full bg-base-100/90 px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums text-accent shadow-sm"
                    style={{
                      left: `clamp(2.75rem, ${barWidthFor(
                        datum[series.key],
                      )}%, calc(100% - 0.25rem))`,
                    }}
                  >
                    {formatValue(
                      percentageFor(datum[series.key]),
                      "percentage",
                      props.currency,
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );

    const renderRanking = () => {
      const series = spec.value.series[0];
      if (!series) return null;
      return (
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.value.map((datum, index) => (
            <div
              key={index}
              class="flex items-center gap-3 rounded-xl bg-base-200/40 p-3.5"
              title={tooltipFor(datum)}
            >
              <span
                class={[
                  "grid h-9 w-9 shrink-0 place-items-center rounded-xl text-sm font-bold tabular-nums",
                  index === 0
                    ? "bg-primary text-primary-content"
                    : "bg-primary/10 text-primary",
                ]}
              >
                {index + 1}
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate font-semibold text-base-content">
                  {String(datum[xKey.value] ?? "—")}
                </span>
                <span class="block text-xs font-medium tabular-nums text-base-content/75">
                  {formatValue(
                    datum[series.key],
                    series.format,
                    props.currency,
                  )}
                </span>
              </span>
              <span class="shrink-0 text-xl font-bold tabular-nums text-primary">
                {formatValue(
                  percentageFor(datum[series.key]),
                  "percentage",
                  props.currency,
                )}
              </span>
            </div>
          ))}
        </div>
      );
    };

    /** `line` / vertical `bar` / `pie` all render through the shared echarts wrapper. */
    const renderLemmonChart = () => (
      <LemmonChart
        type={spec.value.type}
        data={data.value}
        xKey={xKey.value}
        series={spec.value.series.map((s, i) => ({
          key: s.key,
          name: t(s.labelKey),
          format: toChartFormat(s.format),
          color: CHART_PALETTE[i % CHART_PALETTE.length],
        }))}
        currency={props.currency}
        valueAxis={spec.value.valueAxis}
        height={220}
      />
    );

    return () => {
      if (props.loading) {
        return <div class="h-56 animate-pulse rounded-xl bg-base-200" />;
      }
      // An empty chart must say so — a blank panel reads as a broken page.
      if (!data.value.length) {
        return (
          <div class="grid h-40 place-items-center rounded-xl border border-dashed bg-base-100/40 text-center">
            <div>
              <span class="material-symbols-outlined text-3xl text-base-content/35">
                bar_chart
              </span>
              <p class="mt-1 text-sm text-base-content/70">
                {t("reports.dataroom.chart_empty")}
              </p>
            </div>
          </div>
        );
      }

      const isRanking = spec.value.variant === "ranking";
      const isHorizontal =
        spec.value.type === "bar" && spec.value.orientation === "horizontal";

      return (
        <div>
          {(spec.value.titleKey || spec.value.descriptionKey) && (
            <div class="mb-3 flex items-baseline gap-2 hidden">
              {spec.value.titleKey && (
                <h4 class="text-sm font-semibold text-base-content">
                  {t(spec.value.titleKey)}
                </h4>
              )}
              {spec.value.descriptionKey && (
                <span
                  class="material-symbols-outlined cursor-help text-sm text-base-content/30"
                  title={t(spec.value.descriptionKey)}
                >
                  info
                </span>
              )}
            </div>
          )}

          {isRanking
            ? renderRanking()
            : isHorizontal
              ? renderHorizontalBars()
              : renderLemmonChart()}

          {spec.value.xLabelKey && !isRanking && (
            <div class="mt-2 text-center text-[10px] uppercase tracking-wider text-base-content/70">
              {t(spec.value.xLabelKey)}
            </div>
          )}

          {hiddenCount.value > 0 && (
            <div class="mt-2 text-[11px] text-base-content/70">
              {t("reports.dataroom.chart_truncated").replace(
                "{count}",
                String(hiddenCount.value),
              )}
            </div>
          )}

          {isHorizontal && spec.value.series.length > 1 && renderLegend()}
        </div>
      );
    };
  },
});
